import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  AgentCreate,
  AgentUpdate,
  CapabilityCreate,
  CapabilityUpdate,
} from "../../generated/core-api";

export const AGENTS_QUERY_KEY = ["agents"] as const;
export const CAPABILITIES_QUERY_KEY = ["capabilities"] as const;

export function useAgents(includeInactive = false) {
  return useQuery({
    queryKey: [...AGENTS_QUERY_KEY, { includeInactive }],
    queryFn: () => api.listAgents({ include_inactive: includeInactive }),
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AgentCreate) => api.createAgent(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
}

export function useUpdateAgent(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AgentUpdate) => api.updateAgentBlueprint(agentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => api.deleteAgent(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });
}

export function useCapabilities(includeInactive = false) {
  return useQuery({
    queryKey: [...CAPABILITIES_QUERY_KEY, { includeInactive }],
    queryFn: () => api.listCapabilities({ include_inactive: includeInactive }),
  });
}

export function useCreateCapability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CapabilityCreate) => api.createCapability(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPABILITIES_QUERY_KEY });
    },
  });
}

export function useUpdateCapability(capabilityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CapabilityUpdate) =>
      api.updateCapability(capabilityId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPABILITIES_QUERY_KEY });
    },
  });
}

export function useDeleteCapability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capabilityId: string) => api.deleteCapability(capabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAPABILITIES_QUERY_KEY });
    },
  });
}
